import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Exercise, ExerciseState, ExerciseFormData, ExerciseType } from '../../types/exercise';
import {
  getExerciseById,
  getUserExercises,
  addExercise,
  updateExercise,
  deleteExercise,
  getExercises,
  createExercise as createExerciseService,
  updateExerciseFromForm,
  uploadExercisePhoto,
  deleteExercisePhoto
} from '../../services/firebase/exercise.service';

// Initial state
const initialState: ExerciseState = {
  exercises: {},
  userExercises: {},
  currentExercise: null,
  isLoading: false,
  error: null
};

// Async thunks
export const fetchExerciseById = createAsyncThunk<
  Exercise,
  { ownerId: string; exerciseId: string },
  { rejectValue: string }
>('exercise/fetchExerciseById', async ({ ownerId, exerciseId }, { rejectWithValue }) => {
  try {
    const exercise = await getExerciseById(ownerId, exerciseId);
    if (!exercise) {
      return rejectWithValue('Exercise not found');
    }
    return exercise;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch exercise');
  }
});

export const fetchUserExercises = createAsyncThunk<
  { exercises: Record<string, Exercise>; userId: string },
  string,
  { rejectValue: string }
>('exercise/fetchUserExercises', async (userId, { rejectWithValue }) => {
  try {
    const exercises = await getUserExercises(userId);
    return { exercises, userId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch user exercises');
  }
});

export const fetchFilteredExercises = createAsyncThunk<
  { exercises: Exercise[]; userId: string },
  { userId: string; type?: ExerciseType; limit?: number; startDate?: number },
  { rejectValue: string }
>('exercise/fetchFilteredExercises', async ({ userId, type, limit, startDate }, { rejectWithValue }) => {
  try {
    const options = { type, limit, startDate };
    const exercises = await getExercises(userId, options);
    return { exercises, userId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch filtered exercises');
  }
});

export const createExercise = createAsyncThunk<
  { exerciseId: string; userId: string },
  { userId: string; exerciseData: Omit<Exercise, 'exerciseKey' | 'author'> },
  { rejectValue: string }
>('exercise/createExercise', async ({ userId, exerciseData }, { rejectWithValue }) => {
  try {
    const exerciseId = await addExercise(userId, exerciseData);
    return { exerciseId, userId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create exercise');
  }
});

export const createExerciseFromForm = createAsyncThunk<
  { exerciseId: string; userId: string },
  { userId: string; formData: ExerciseFormData },
  { rejectValue: string }
>('exercise/createExerciseFromForm', async ({ userId, formData }, { rejectWithValue }) => {
  try {
    const exerciseId = await createExerciseService(userId, formData);
    return { exerciseId, userId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create exercise from form data');
  }
});

export const editExercise = createAsyncThunk<
  { userId: string; exerciseId: string },
  { userId: string; exerciseId: string; updatedData: Partial<Exercise> },
  { rejectValue: string }
>('exercise/editExercise', async ({ userId, exerciseId, updatedData }, { rejectWithValue }) => {
  try {
    await updateExercise(userId, exerciseId, updatedData);
    return { userId, exerciseId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update exercise');
  }
});

export const editExerciseFromForm = createAsyncThunk<
  { userId: string; exerciseId: string },
  { userId: string; exerciseId: string; formData: Partial<ExerciseFormData> },
  { rejectValue: string }
>('exercise/editExerciseFromForm', async ({ userId, exerciseId, formData }, { rejectWithValue }) => {
  try {
    await updateExerciseFromForm(userId, exerciseId, formData);
    return { userId, exerciseId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update exercise from form data');
  }
});

export const removeExercise = createAsyncThunk<
  { userId: string; exerciseId: string },
  { userId: string; exerciseId: string },
  { rejectValue: string }
>('exercise/removeExercise', async ({ userId, exerciseId }, { rejectWithValue }) => {
  try {
    await deleteExercise(userId, exerciseId);
    return { userId, exerciseId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete exercise');
  }
});

// Like functionality removed - use the unified likes.service.ts instead

export const uploadExercisePhotoThunk = createAsyncThunk<
  { photo: any; userId: string; exerciseId: string },
  { userId: string; exerciseId: string; photoFile: File },
  { rejectValue: string }
>('exercise/uploadPhoto', async ({ userId, exerciseId, photoFile }, { rejectWithValue }) => {
  try {
    const photo = await uploadExercisePhoto(userId, exerciseId, photoFile);
    return { photo, userId, exerciseId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to upload exercise photo');
  }
});

export const deleteExercisePhotoThunk = createAsyncThunk<
  { photoId: string; userId: string; exerciseId: string },
  { userId: string; exerciseId: string; photoId: string },
  { rejectValue: string }
>('exercise/deletePhoto', async ({ userId, exerciseId, photoId }, { rejectWithValue }) => {
  try {
    await deleteExercisePhoto(userId, exerciseId, photoId);
    return { photoId, userId, exerciseId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete exercise photo');
  }
});

// Exercise slice
const exerciseSlice = createSlice({
  name: 'exercise',
  initialState,
  reducers: {
    clearCurrentExercise: (state) => {
      state.currentExercise = null;
    },
    clearExercisesError: (state) => {
      state.error = null;
    },
    setCurrentExercise: (state, action: PayloadAction<Exercise>) => {
      state.currentExercise = action.payload;
    },
    updateExerciseInState: (state, action: PayloadAction<Exercise>) => {
      const exercise = action.payload;
      if (exercise.exerciseKey) {
        state.exercises[exercise.exerciseKey] = exercise;

        // Also update currentExercise if it's the same one
        if (state.currentExercise?.exerciseKey === exercise.exerciseKey) {
          state.currentExercise = exercise;
        }
      }
    },
    // Like functionality removed - use the unified likes.service.ts instead
    addExercisePhoto: (state, action: PayloadAction<{ exerciseId: string; photo: any }>) => {
      const { exerciseId, photo } = action.payload;

      // Update in exercises list (photos stored as Record with numeric keys)
      if (state.exercises[exerciseId]) {
        if (!state.exercises[exerciseId].photos) {
          state.exercises[exerciseId].photos = {};
        }
        const nextKey = Object.keys(state.exercises[exerciseId].photos!).length.toString();
        state.exercises[exerciseId].photos![nextKey] = photo;
      }

      // Update in current exercise if loaded
      if (state.currentExercise && state.currentExercise.exerciseKey === exerciseId) {
        if (!state.currentExercise.photos) {
          state.currentExercise.photos = {};
        }
        const nextKey = Object.keys(state.currentExercise.photos).length.toString();
        state.currentExercise.photos[nextKey] = photo;
      }
    },
    removeExercisePhoto: (state, action: PayloadAction<{ exerciseId: string; photoId: string }>) => {
      const { exerciseId, photoId } = action.payload;

      // Update in exercises list (photoId is the key in the Record)
      if (state.exercises[exerciseId]?.photos) {
        delete state.exercises[exerciseId].photos![photoId];
      }

      // Update in current exercise if loaded
      if (state.currentExercise?.exerciseKey === exerciseId && state.currentExercise.photos) {
        delete state.currentExercise.photos[photoId];
      }
    },
    updateExerciseCommentCount: (state, action: PayloadAction<{ exerciseId: string; count: number }>) => {
      const { exerciseId, count } = action.payload;

      // Update in exercises list
      if (state.exercises[exerciseId]) {
        state.exercises[exerciseId].commentsCount = count;
      }

      // Update in current exercise if loaded
      if (state.currentExercise && state.currentExercise.exerciseKey === exerciseId) {
        state.currentExercise.commentsCount = count;
      }
    }
  },
  extraReducers: (builder) => {
    // fetchExerciseById
    builder.addCase(fetchExerciseById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchExerciseById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentExercise = action.payload;

      // Also update in exercises map
      if (action.payload.exerciseKey) {
        state.exercises[action.payload.exerciseKey] = action.payload;
      }
    });
    builder.addCase(fetchExerciseById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // fetchUserExercises
    builder.addCase(fetchUserExercises.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserExercises.fulfilled, (state, action) => {
      const { exercises, userId } = action.payload;
      state.isLoading = false;
      state.exercises = { ...state.exercises, ...exercises };

      // Update userExercises map
      const exerciseKeys = Object.keys(exercises);
      state.userExercises[userId] = exerciseKeys;
    });
    builder.addCase(fetchUserExercises.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // fetchFilteredExercises
    builder.addCase(fetchFilteredExercises.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchFilteredExercises.fulfilled, (state, action) => {
      state.isLoading = false;
      const { exercises, userId } = action.payload;

      // Convert array to record and merge with state.exercises
      const exercisesRecord: Record<string, Exercise> = {};
      const exerciseKeys: string[] = [];

      exercises.forEach(exercise => {
        if (exercise.exerciseKey) {
          exercisesRecord[exercise.exerciseKey] = exercise;
          exerciseKeys.push(exercise.exerciseKey);
        }
      });

      state.exercises = { ...state.exercises, ...exercisesRecord };

      // Update userExercises with the new keys
      if (!state.userExercises[userId]) {
        state.userExercises[userId] = [];
      }

      // Only add keys that don't already exist
      exerciseKeys.forEach(key => {
        if (!state.userExercises[userId].includes(key)) {
          state.userExercises[userId].push(key);
        }
      });
    });
    builder.addCase(fetchFilteredExercises.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // createExercise
    builder.addCase(createExercise.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createExercise.fulfilled, (state, action) => {
      state.isLoading = false;
      const { exerciseId, userId } = action.payload;

      // Update userExercises map
      if (!state.userExercises[userId]) {
        state.userExercises[userId] = [];
      }

      if (!state.userExercises[userId].includes(exerciseId)) {
        state.userExercises[userId].push(exerciseId);
      }
    });
    builder.addCase(createExercise.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // createExerciseFromForm
    builder.addCase(createExerciseFromForm.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createExerciseFromForm.fulfilled, (state, action) => {
      state.isLoading = false;
      const { exerciseId, userId } = action.payload;

      // Update userExercises map
      if (!state.userExercises[userId]) {
        state.userExercises[userId] = [];
      }

      if (!state.userExercises[userId].includes(exerciseId)) {
        state.userExercises[userId].push(exerciseId);
      }
    });
    builder.addCase(createExerciseFromForm.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // editExercise and editExerciseFromForm (shared pattern)
    const handleExerciseEdit = (state: ExerciseState, action: PayloadAction<{ userId: string; exerciseId: string }>) => {
      state.isLoading = false;
      // We'll handle the state update when we fetch the exercise again or via updateExerciseInState
    };

    // editExercise
    builder.addCase(editExercise.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(editExercise.fulfilled, handleExerciseEdit);
    builder.addCase(editExercise.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // editExerciseFromForm
    builder.addCase(editExerciseFromForm.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(editExerciseFromForm.fulfilled, handleExerciseEdit);
    builder.addCase(editExerciseFromForm.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // removeExercise
    builder.addCase(removeExercise.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(removeExercise.fulfilled, (state, action) => {
      state.isLoading = false;
      const { userId, exerciseId } = action.payload;

      // Remove from exercises map
      delete state.exercises[exerciseId];

      // Remove from userExercises map
      if (state.userExercises[userId]) {
        state.userExercises[userId] = state.userExercises[userId].filter(id => id !== exerciseId);
      }

      // Clear currentExercise if it's the deleted one
      if (state.currentExercise?.exerciseKey === exerciseId) {
        state.currentExercise = null;
      }
    });
    builder.addCase(removeExercise.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Unknown error occurred';
    });

    // Like functionality removed - use the unified likes.service.ts instead
    // The following builder cases have been removed:
    // - likeExercise.fulfilled
    // - checkExerciseLikeStatus.fulfilled

    // uploadExercisePhotoThunk
    builder.addCase(uploadExercisePhotoThunk.fulfilled, (state, action) => {
      const { photo, exerciseId } = action.payload;

      // Update in exercises list (photos stored as Record with numeric keys)
      if (state.exercises[exerciseId]) {
        if (!state.exercises[exerciseId].photos) {
          state.exercises[exerciseId].photos = {};
        }
        const nextKey = Object.keys(state.exercises[exerciseId].photos!).length.toString();
        state.exercises[exerciseId].photos![nextKey] = photo;
      }

      // Update in current exercise if loaded
      if (state.currentExercise?.exerciseKey === exerciseId) {
        if (!state.currentExercise.photos) {
          state.currentExercise.photos = {};
        }
        const nextKey = Object.keys(state.currentExercise.photos).length.toString();
        state.currentExercise.photos[nextKey] = photo;
      }
    });

    // deleteExercisePhotoThunk
    builder.addCase(deleteExercisePhotoThunk.fulfilled, (state, action) => {
      const { photoId, exerciseId } = action.payload;

      // Update in exercises list (photoId is the key in the Record)
      if (state.exercises[exerciseId]?.photos) {
        delete state.exercises[exerciseId].photos![photoId];
      }

      // Update in current exercise if loaded
      if (state.currentExercise?.exerciseKey === exerciseId && state.currentExercise.photos) {
        delete state.currentExercise.photos[photoId];
      }
    });
  }
});

export const {
  clearCurrentExercise,
  clearExercisesError,
  setCurrentExercise,
  updateExerciseInState,
  addExercisePhoto,
  removeExercisePhoto,
  updateExerciseCommentCount
} = exerciseSlice.actions;

export default exerciseSlice.reducer;